"use no memo";

import React from "react";
import "./CoverAudienceOverlap.scss";

import RGL, { WidthProvider } from "react-grid-layout";
import { isEqual } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import AudienceOverlapInfoState from "src/app/components/AudienceOverlap/AudienceOverlapInfoState";
import { Button } from "src/app/components/Button/Button";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { GridBox } from "src/app/generator-data/components/GridBox/GridBox";
import { OverlapChartBox } from "src/app/ReportGenerator/components/ReportTemplate/components/OverlapChartBox/OverlapChartBox";
import { OverlapPlatformBox } from "src/app/ReportGenerator/components/ReportTemplate/components/OverlapPlatformBox/OverlapPlatformBox";
import { IDH_BRAND_COLOR } from "src/constants/idhBrandColor";
import { ReactComponent as InfoIcon } from "src/images/info-new.svg";
import { ReactComponent as LoaderDots } from "src/images/loader-dots.svg";
import { setShowcaseAudienceOverlap } from "src/redux";
import { ShowcaseSeparator } from "../ShowcaseTemplate/components/ShowcaseSeparator/ShowcaseSeparator";
import { AUDIENCE_OVERLAP_GRID } from "./audienceOverlapUtils";
import { OverlapCreatorRow } from "./OverlapCreatorRow";
import { useShowcaseAudienceOverlap } from "./useShowcaseAudienceOverlap";

// !!! THIS MUST BE DECLARED OUTSIDE OF COMPONENT !!!
const ReactGridLayout = WidthProvider(RGL);
// !!! THIS MUST BE DECLARED OUTSIDE OF COMPONENT !!!

const toLayoutItem = ({ i, x, y, w, h }) => ({ i, x, y, w, h });

const renderBoxContent = (box) => {
  switch (box.type) {
    case "header":
      return (
        <div className="cover-audience-overlap__header">
          <IDHFormattedMessage
            id="ws_audience_overlap"
            defaultMessage="Audience overlap"
          />
        </div>
      );
    case "platform":
      return <OverlapPlatformBox data={box} />;
    case "chart":
      return (
        <OverlapChartBox
          animated={false}
          data={{
            overlapData: { audienceOverlap: { reportInfo: box.reportInfo } },
          }}
        />
      );
    case "creator":
    case "warning":
      return <OverlapCreatorRow box={box} />;
    default:
      return null;
  }
};

function AudienceOverlapPage({ children, printing }) {
  const identity = useSelector((state) => state.mainReducer.identity);
  const primaryColor =
    identity.wsWorkspace?.customPrimaryColor || IDH_BRAND_COLOR;

  return (
    <div className="cover-audience-overlap__page">
      <ShowcaseSeparator
        className="cover-audience-overlap__top-separator"
        hideInfo
        printing={printing}
      />

      <div className="cover-audience-overlap__page-content">{children}</div>

      <ShowcaseSeparator
        className="cover-audience-overlap__bottom-separator"
        primaryColor={primaryColor}
        provider="last"
        printing={printing}
      />
    </div>
  );
}

function AudienceOverlapInfoPage({ icon, title, text, children }) {
  return (
    <AudienceOverlapPage>
      <AudienceOverlapInfoState icon={icon} title={title} text={text}>
        {children}
      </AudienceOverlapInfoState>
    </AudienceOverlapPage>
  );
}

export function CoverAudienceOverlap({ printing }) {
  const dispatch = useDispatch();

  const { available, status, run } = useShowcaseAudienceOverlap();

  const pages = useSelector(
    (state) => state.showcaseReducer.audienceOverlap.pages,
  );
  const boxes = useSelector(
    (state) => state.showcaseReducer.audienceOverlap.boxes,
  );
  const loading = useSelector(
    (state) =>
      state.showcaseReducer.generating || state.showcaseReducer.printing,
  );

  if (!available) return null;

  const setPageLayout = (pageId, newLayout) => {
    const newPages = pages
      .map((page) =>
        page.id === pageId
          ? { ...page, layout: newLayout.map(toLayoutItem) }
          : page,
      )
      // a page without any box would be an empty page in the downloaded showcase
      .filter((page) => page.layout.length);

    if (isEqual(newPages, pages)) return;

    dispatch(setShowcaseAudienceOverlap({ pages: newPages }));
  };

  const removeBox = (page, boxId) => {
    setPageLayout(
      page.id,
      page.layout.filter((item) => item.i !== boxId),
    );
  };

  if (status === "done") {
    return (
      <div className="cover-audience-overlap">
        {pages.map((page) => (
          <AudienceOverlapPage key={page.id} printing={printing}>
            <ReactGridLayout
              className="cover-audience-overlap__layout"
              layout={page.layout}
              cols={AUDIENCE_OVERLAP_GRID.cols}
              rowHeight={AUDIENCE_OVERLAP_GRID.rowHeight}
              maxRows={AUDIENCE_OVERLAP_GRID.rows}
              margin={AUDIENCE_OVERLAP_GRID.margin}
              containerPadding={[0, 0]}
              compactType={null}
              preventCollision
              autoSize={false}
              onLayoutChange={(newLayout) => setPageLayout(page.id, newLayout)}
              draggableHandle=".react-drag-handle"
            >
              {page.layout
                .filter((item) => boxes[item.i])
                .map((item) => (
                  <GridBox
                    key={item.i}
                    id={item.i}
                    className={`cover-audience-overlap__box cover-audience-overlap__box--${boxes[item.i].type}`}
                    removeFromLayout={() => removeBox(page, item.i)}
                    disableEditing
                  >
                    {renderBoxContent(boxes[item.i])}
                  </GridBox>
                ))}
            </ReactGridLayout>
          </AudienceOverlapPage>
        ))}
      </div>
    );
  }

  // only the calculated overlap is a part of the downloaded showcase
  if (printing) return null;

  if (status === "pending") {
    return (
      <div className="cover-audience-overlap">
        <AudienceOverlapInfoPage
          icon={<LoaderDots />}
          title={
            <IDHFormattedMessage
              id="ws_analyzing_audience_overlap"
              defaultMessage="Analyzing audience overlap..."
            />
          }
          text={
            <IDHFormattedMessage
              id="ws_this_will_only_take_a_few_seconds"
              defaultMessage="This will only take a few seconds."
            />
          }
        />
      </div>
    );
  }

  return (
    <div className="cover-audience-overlap">
      <AudienceOverlapInfoPage
        icon={<InfoIcon />}
        title={
          <IDHFormattedMessage
            id="ws_audience_overlap_is_off"
            defaultMessage="Audience overlap is off"
          />
        }
        text={
          <IDHFormattedMessage
            id="ws_add_it_to_show_how_much_your_creators_audiences_overlap"
            defaultMessage="Add it to show how much your creators' audiences overlap."
          />
        }
      >
        <Button
          size="medium"
          variant="white-with-grey-border"
          disabled={loading}
          onClick={run}
        >
          <IDHFormattedMessage
            id="ws_run_analysis"
            defaultMessage="Run analysis"
          />
        </Button>
      </AudienceOverlapInfoPage>
    </div>
  );
}
